import { useEffect, useState } from 'react';
import posthog from 'posthog-js';
import { useMeasurementConsent } from './use-measurement-consent';

/**
 * Consent-gated replacement for posthog's `useFeatureFlagVariantKey`.
 *
 * Users without the Measurement consent are cookieless-anonymous: their
 * server-side hash rotates daily, so a flag-based experiment would flip them
 * between arms day over day (incoherent UX, noisy data). This hook keeps them
 * OUT of experiments entirely: without consent it returns `undefined` — which
 * every experiment hook maps to its control arm — and, crucially, it never
 * reads the flag, so no `$feature_flag_called` exposure event is emitted and
 * the experiment analysis never counts them.
 *
 * Why not wrap `useFeatureFlagVariantKey` directly: React hooks cannot be
 * called conditionally, and calling it unconditionally would register the
 * exposure even for users we then force to control. Reading the flag
 * imperatively inside a consent-gated effect is what keeps the data clean.
 */
export function useConsentedFeatureFlagVariantKey(flagKey: string): string | boolean | undefined {
  const consented = useMeasurementConsent();
  const [variant, setVariant] = useState<string | boolean | undefined>(undefined);

  useEffect(() => {
    if (!consented) return undefined;
    // getFeatureFlag emits the $feature_flag_called exposure event — deliberately
    // reachable only from this consented branch.
    const readFlag = () => setVariant(posthog.getFeatureFlag(flagKey));
    const unsubscribe = posthog.onFeatureFlags(readFlag);
    if (posthog.__loaded) readFlag();
    return unsubscribe;
  }, [consented, flagKey]);

  return consented ? variant : undefined;
}
